import StatusBadgeM from "@common/components/badge/StatusBadgeM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { capitalize } from "@data/utility/Utils"
import { useNavigation } from "@react-navigation/native"
import moment from "moment"
import React, { useContext } from "react"
import { View } from "react-native"
import CircularProgress from "react-native-circular-progress-indicator/src/circularProgress/index"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { SchoolSubjectBadge } from "../../../tutor/students/ProfilDetails"

/**
 * LiveReportCardComponent - Reusable card component for displaying live session reports
 */
const LiveReportCardComponent = (props) => {
    const { report, style } = props

    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)

    // Extract session date and time
    const sessionDate =
        report?.statistics?.live_date_time ?? report?.live_occurrence?.date
    const formattedDate = sessionDate
        ? capitalize(moment(sessionDate).format("dddd DD MMMM YYYY"))
        : "-"
    const formattedTime = sessionDate
        ? moment(sessionDate).format("HH:mm")
        : "-"

    // Calculate duration in minutes from live_connection_duration (in seconds)
    const durationInSeconds = report?.statistics?.live_connection_duration ?? 0
    const duration = Math.round(durationInSeconds / 60)

    // Get tutor name
    const tutorName = report?.tutor_display_name

    // Get engagement score
    const totalScore = report?.statistics?.scores?.total_score ?? null

    // Determine engagement level text and color
    const getEngagementLevel = (score) => {
        if (score === null || score === undefined)
            return { text: "-", color: theme.colors.grey }
        if (score >= 90)
            return { text: "Excellent", color: theme.colors.primary }
        if (score >= 70)
            return { text: "Très bien", color: theme.colors.success }
        if (score >= 50) return { text: "Bien", color: theme.colors.warning }
        return { text: "À améliorer", color: theme.colors.error }
    }

    const engagementLevel = getEngagementLevel(totalScore)

    // Check if student was absent (no statistics)
    const isAbsent = !report?.statistics

    // Get ongoing chapters and their subjects
    const ongoingChapters = report?.statistics?.ongoing_student_chapters ?? []

    // Create a map of unique subject + chapter combinations
    const chaptersWithSubjects = ongoingChapters
        .map((studentChapter) => {
            const chapter = studentChapter?.chapter
            const schoolSubjectId = chapter?.school_subject_id

            // Find the school subject from Redux state
            const schoolSubject = props.schoolSubjects?.find(
                (subject) => subject?.id === schoolSubjectId
            )

            return {
                subjectName: schoolSubject?.name ?? "Matière inconnue",
                chapterName: chapter?.name ?? "",
                schoolSubject: schoolSubject,
            }
        })
        .filter((item) => item.chapterName) // Only keep items with chapter names

    // Tutor name + replacement/rattrapage badges (shared between absent & present states)
    const tutorBadges = (tutorName ||
        report?.live_occurrence?.is_replacement ||
        report?.is_rattrapage) && (
        <View
            style={{
                flexDirection: "row",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 6,
            }}
        >
            {tutorName && (
                <TextM fontSize={14} color={theme.colors.grey}>
                    Avec {tutorName}
                </TextM>
            )}
            {report?.live_occurrence?.is_replacement && (
                <StatusBadgeM
                    icon="user-friends"
                    label="Remplacement"
                    backgroundColor={theme.colors.warningLight}
                    color={theme.colors.warning}
                />
            )}
            {report?.is_rattrapage && (
                <StatusBadgeM
                    icon="redo"
                    label="Rattrapage"
                    backgroundColor={theme.colors.primaryLighter}
                    color={theme.colors.primary}
                />
            )}
        </View>
    )

    // Calculate card width to match FilledButtonM default width
    const width =
        dimensions?.width < 800 ? dimensions?.width - 40 : dimensions?.width / 2

    return (
        <View
            style={{
                backgroundColor: theme.colors.white,
                borderRadius: 15,
                borderWidth: 1,
                borderColor: theme.colors.lightMore,
                padding: 20,
                marginBottom: 15,
                maxWidth: 600,
                alignSelf: "center",
                ...style,
            }}
        >
            {/* Date and Time */}
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 10,
                    flexWrap: "wrap",
                }}
            >
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginRight: 12,
                        marginBottom: 4,
                    }}
                >
                    <FontAwesome5
                        name="calendar"
                        size={14}
                        color={theme.colors.grey}
                        style={{ marginRight: 6 }}
                    />
                    <TextM fontSize={13} color={theme.colors.grey}>
                        {formattedDate}
                    </TextM>
                </View>
                {!isAbsent && (
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            marginBottom: 4,
                        }}
                    >
                        <FontAwesome5
                            name="clock"
                            size={14}
                            color={theme.colors.grey}
                            style={{ marginRight: 6 }}
                        />
                        <TextM fontSize={13} color={theme.colors.grey}>
                            {formattedTime} • {duration} min
                        </TextM>
                    </View>
                )}
            </View>

            {/* Main Content */}
            {isAbsent ? (
                /* Absent State */
                <View style={{ marginBottom: 10, width: width }}>
                    {/* Tutor Name + Badges */}
                    {tutorBadges && (
                        <View style={{ marginBottom: 12 }}>{tutorBadges}</View>
                    )}

                    {/* Absent Badge */}
                    <View
                        style={{
                            backgroundColor: theme.colors.error + "11",
                            borderRadius: 10,
                            borderWidth: 1,
                            borderColor: theme.colors.error + "33",
                            paddingVertical: 8,
                            paddingHorizontal: 12,
                            alignSelf: "flex-start",
                        }}
                    >
                        <TextM
                            fontSize={14}
                            fontWeight="SemiBold"
                            color={theme.colors.error}
                        >
                            Absent
                        </TextM>
                    </View>
                </View>
            ) : (
                /* Present State - Split into two columns */
                <View
                    style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 10,
                    }}
                >
                    {/* Left Column - Session Info */}
                    <View style={{ flex: 1, paddingRight: 15 }}>
                        {/* Chapters worked on as badges */}
                        {chaptersWithSubjects.length > 0 && (
                            <View
                                style={{
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    marginBottom: 10,
                                }}
                            >
                                {chaptersWithSubjects.map((item, index) => (
                                    <SchoolSubjectBadge
                                        key={index}
                                        schoolSubject={item.schoolSubject}
                                        chapterName={item.chapterName}
                                        multiline={true}
                                        iconWidth={14}
                                        fontSize={11}
                                        style={{ marginBottom: 5 }}
                                    />
                                ))}
                            </View>
                        )}

                        {/* Tutor Name + Badges */}
                        {tutorBadges}
                    </View>

                    {/* Right Column - Score Circle */}
                    <View
                        style={{
                            alignItems: "center",
                            minWidth: 100,
                            flexShrink: 0,
                        }}
                    >
                        <View style={{ position: "relative" }}>
                            <CircularProgress
                                value={totalScore ?? 0}
                                radius={40}
                                maxValue={100}
                                title={`${totalScore ?? "-"}`}
                                titleStyle={{
                                    fontSize: 22,
                                    fontFamily: "Montserrat-ExtraBold",
                                }}
                                activeStrokeColor={engagementLevel.color}
                                inActiveStrokeColor={theme.colors.lightMore}
                                activeStrokeWidth={5}
                                inActiveStrokeWidth={5}
                                showProgressValue={false}
                            />
                            <View
                                style={{
                                    position: "absolute",
                                    width: "100%",
                                    height: "100%",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    paddingTop: 22,
                                    marginTop: 8,
                                }}
                            >
                                <TextM fontSize={9} color={theme.colors.grey}>
                                    /100
                                </TextM>
                            </View>
                        </View>
                        <View
                            style={{
                                alignItems: "center",
                                marginTop: 6,
                                maxWidth: 100,
                            }}
                        >
                            <TextM
                                fontSize={10}
                                color={theme.colors.grey}
                                style={{ marginBottom: 2 }}
                            >
                                Engagement
                            </TextM>
                            <TextM
                                fontSize={12}
                                fontWeight="Bold"
                                color={engagementLevel.color}
                                style={{ textAlign: "center" }}
                                numberOfLines={2}
                            >
                                {engagementLevel.text}
                            </TextM>
                        </View>
                    </View>
                </View>
            )}

            {/* View Full Report Button - Only show if not absent */}
            {!isAbsent && (
                <FilledButtonM
                    mode="outlined"
                    label="Voir le rapport complet"
                    color={theme.colors.primary}
                    onPress={() => navigation.push("r", { h: report?.hash })}
                    labelStyle={{
                        fontSize: 12,
                        height: 16,
                    }}
                    contentStyle={{
                        height: 30,
                        paddingHorizontal: 12,
                    }}
                    style={{
                        marginTop: 10,
                        alignSelf: "stretch",
                        maxWidth: "100%",
                    }}
                />
            )}
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        schoolSubjects: state.data.staticData?.school_subjects ?? [],
    }
}

export default connect(mapStateToProps)(LiveReportCardComponent)
